import { useMutation } from "@tanstack/react-query";
import React from "react";
import useGoogleMapsAddress from "../../../hooks/useGoogleMapsAddress";
import AutoCompleteDialog from "../../AutoCompleteDialog";
import { translate } from "../../../translate";
import { STRINGS } from "../../../strings";

interface Props {
  isOpen: boolean;
  placeholder?: string;
  defaultValue?: string;
  onClickPlace: (place: google.maps.places.PlaceResult) => void;
  onRequestClose: () => void;
}

function useAddressDialog(params: {
  defaultValue?: string;
  onClickPlace: (place: google.maps.places.PlaceResult) => void;
}) {
  const { handleChange, placePredictions, input, getDetails, showPredictions, setShowPredictions } =
    useGoogleMapsAddress({ defaultValue: params.defaultValue });

  const getDetailsMutation = useMutation(getDetails, {
    onMutate: () => setShowPredictions(false),
    onSuccess: (data) => {
      if (data !== null) {
        params.onClickPlace(data);
      }
    },
  });

  const predictions = React.useMemo(() => {
    return placePredictions.map((prediction) => ({
      id: prediction.place_id,
      title: prediction.description,
      ...prediction,
    }));
  }, [placePredictions]);

  return {
    getDetailsMutation,
    showPredictions,
    placePredictions: predictions,
    input: input,
    onChange: handleChange,
  };
}

const AddressDialog = (props: Props) => {
  const { input, getDetailsMutation, placePredictions, showPredictions, onChange } =
    useAddressDialog({
      onClickPlace: props.onClickPlace,
      defaultValue: props.defaultValue,
    });

  const handleClickPrediction = (prediction: google.maps.places.AutocompletePrediction) => {
    getDetailsMutation.mutate(prediction);
  };

  return (
    <AutoCompleteDialog
      isLoading={getDetailsMutation.isLoading}
      isOpen={props.isOpen}
      query={input}
      placeholder={props.placeholder ?? translate(STRINGS.ADDRESS)}
      showPredictions={showPredictions}
      predictions={placePredictions}
      onChange={onChange}
      onClickPrediction={handleClickPrediction}
      onRequestClose={props.onRequestClose}
    />
  );
};

export default AddressDialog;
